import { useMemo } from 'react';
import { Box, Avatar, Chip, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DataGridPremium, GridToolbar } from '@mui/x-data-grid-premium';
import { AVATAR_BG, AVATAR_FG, initials, CONNECTED_HELP_TEXT } from './constants';

// Landing view: every provider in the shared dictionary, filterable by name
// and — via the grid's own filter panel — by type, client count, connected
// count. Click a row to open that provider's clients.

const ProviderGrid = ({ providers, onOpen }) => {
    const typeOptions = useMemo(
        () => Array.from(new Set(providers.map((p) => p.type).filter(Boolean))).sort(),
        [providers],
    );
    const categoryOptions = useMemo(
        () => Array.from(new Set(providers.map((p) => p.category).filter(Boolean))).sort(),
        [providers],
    );

    const columns = useMemo(() => [
        {
            field: 'name', headerName: 'Provider', flex: 1.4, minWidth: 240,
            renderCell: (p) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: '100%' }}>
                    <Avatar sx={{ bgcolor: AVATAR_BG, color: AVATAR_FG, width: 30, height: 30, fontSize: 12, fontWeight: 700 }}>
                        {initials(p.row.name)}
                    </Avatar>
                    <Box sx={{ fontSize: 13.5, fontWeight: 600 }}>{p.row.name}</Box>
                </Box>
            ),
        },
        {
            field: 'category', headerName: 'Category', width: 160, type: 'singleSelect', valueOptions: categoryOptions,
            renderCell: (p) => (p.value ? (
                <Chip label={p.value} size="small" variant="outlined" color="primary" sx={{ height: 22, fontSize: 11.5 }} />
            ) : null),
        },
        {
            field: 'type', headerName: 'Type', width: 170, type: 'singleSelect', valueOptions: typeOptions,
            renderCell: (p) => (p.value ? (
                <Chip label={p.value} size="small" variant="outlined" sx={{ height: 22, fontSize: 11.5 }} />
            ) : null),
        },
        { field: 'clientCount', headerName: 'Clients', width: 100, type: 'number' },
        {
            field: 'connectedCount', width: 130, type: 'number',
            renderHeader: () => (
                <Tooltip title={CONNECTED_HELP_TEXT} arrow placement="top">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, fontWeight: 700, fontSize: 14 }}>
                        Connected <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    </Box>
                </Tooltip>
            ),
            renderCell: (p) => (
                <Chip
                    size="small"
                    label={p.value}
                    color={p.value > 0 ? 'success' : 'default'}
                    variant={p.value > 0 ? 'filled' : 'outlined'}
                    sx={{ height: 22, fontSize: 11.5, minWidth: 34 }}
                />
            ),
        },
    ], [typeOptions, categoryOptions]);

    return (
        <Box sx={{ width: '100%' }}>
            <DataGridPremium
                rows={providers}
                columns={columns}
                getRowId={(r) => r.id}
                onRowClick={(p) => onOpen(p.row.id)}
                disableRowSelectionOnClick
                rowHeight={56}
                autoHeight
                pageSizeOptions={[25, 50, 100]}
                initialState={{
                    pagination: { paginationModel: { pageSize: 25 } },
                    sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
                }}
                slots={{ toolbar: GridToolbar }}
                slotProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 250, placeholder: 'Filter by provider name…' } } }}
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                    '& .MuiDataGrid-columnHeaders': { fontWeight: 700 },
                    '& .MuiDataGrid-row': { cursor: 'pointer' },
                    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                }}
            />
        </Box>
    );
};

export default ProviderGrid;
